import type { FontSizeConfig } from '@/models/font-size-config.interface';

type SortOrder = 'asc' | 'desc';

const fontSizeMap = new Map<string, number>(); // Cache for calculated font sizes
const noShrinkSetCache = new Map<string, Set<string>>();

export function safeParseNumber(number: any) {
  return Number(number) || 0;
}

export function sortAlphabetically(elements: string[]) {
  return elements.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
}

export function getBoxImageProxyUrl(imageUrl: string) {
  const boxRegexPattern = new RegExp('^https?://on.box.com');
  const isBoxUrl = boxRegexPattern.test(imageUrl);
  const proxy = import.meta.env.VITE_APP_boxProxy;

  if (!isBoxUrl || !proxy) {
    return imageUrl;
  }

  return imageUrl.replace(boxRegexPattern, proxy);
}

export function compareDates(a: string, b: string, sortOrder: SortOrder = 'asc'): number {
  if (sortOrder === 'asc') {
    return new Date(a).valueOf() - new Date(b).valueOf();
  }

  return new Date(b).valueOf() - new Date(a).valueOf();
}

export function getOrCreate<K, V>(map: Map<K, V>, key: K, factory: () => V): V {
  if (!map.has(key)) {
    map.set(key, factory());
  }

  return map.get(key)!;
}

export function isURL(url: string): boolean {
  try {
    const isProtocolRelative = url.startsWith('//');
    isProtocolRelative ? new URL(url, location.href) : new URL(url);

    return true;
  } catch {
    return false;
  }
}

export function renderTrackingButton(params: any): string {
  const value = params.value || params?.data?.trackingLink;

  if (isURL(value)) {
    return `<button class="tracking-button justified-center"></button>`;
  }

  return value ?? '';
}

export function handleTrackingLinkClick(params: any) {
  if (isURL(params.value)) {
    const newWindow = window.open(params.value, '_blank');

    newWindow?.focus();
  }
}

/**
 * Get the dynamic font size based on the text length.
 * Only applies dynamic sizing if text length is more than the minimum threshold.
 * If the text matches any entry in `noShrinkValues`, the base size is always returned.
 * @param text The text to measure.
 * @param sizeConfig Configuration for font size calculation.
 * @returns The calculated font size as a string with 'px' suffix.
 */
export const determineResponsiveFontSize = (text: string | undefined, sizeConfig: FontSizeConfig): string => {
  if (!text) {
    return sizeConfig.base + 'px';
  }

  const textLength = text.length;
  const cacheKey = `${textLength}:${sizeConfig.base}:${sizeConfig.min}:${sizeConfig.threshold}:${sizeConfig.step}`;

  const noShrinkResult = handleNoShrinkValues(text, sizeConfig);

  if (noShrinkResult) {
    return noShrinkResult;
  }

  if (textLength <= sizeConfig.threshold) {
    return sizeConfig.base + 'px';
  }

  const adjustedSize = getOrCreate(fontSizeMap, cacheKey, () =>
    Math.max(sizeConfig.min, sizeConfig.base - (textLength - sizeConfig.threshold + 1) * sizeConfig.step),
  );

  return adjustedSize + 'px';
};

const handleNoShrinkValues = (text: string, sizeConfig: FontSizeConfig): string | undefined => {
  if (!sizeConfig.noShrinkValues || sizeConfig.noShrinkValues?.length === 0) {
    return;
  }

  const comparisonText = sizeConfig.caseInsensitive ? text.toLowerCase() : text;
  const cacheKey = `${sizeConfig.caseInsensitive}:${sizeConfig.noShrinkValues.join(',')}`;

  let noShrinkSet = noShrinkSetCache.get(cacheKey);

  if (!noShrinkSet) {
    noShrinkSet = new Set(
      sizeConfig.noShrinkValues.map((sizeValue) => (sizeConfig.caseInsensitive ? sizeValue.toLowerCase() : sizeValue)),
    );

    noShrinkSetCache.set(cacheKey, noShrinkSet);
  }

  if (noShrinkSet.has(comparisonText)) {
    return sizeConfig.base + 'px';
  }
};
